import 'dayjs/locale/nl';
import 'forms/helpers/dateTransform';
import 'site/googleTranslateFix';
import 'site/translations';
import 'regenerator-runtime/runtime';

import { ApolloProvider } from '@apollo/client';
import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';
import { ThemeProvider as TitanThemeProvider } from '@rulebooks/evidend-titan';
import { GlobalError } from 'components/GlobalError/GlobalError';
import { GlobalSuccess } from 'components/GlobalSuccess/GlobalSuccess';
import { LeavePromptProvider } from 'components/LeavePrompt';
import { OnboardingProvider } from 'components/Onboarding/OnboardingProvider';
import { config } from 'config';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useApollo } from 'hooks/useApollo';
// import { useAuthClientUrl } from 'hooks/useAuthClientUrl';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { Auth0Provider } from '@auth0/auth0-react';
import { WithAuth } from 'providers/WithAuth';
import React, { useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { customComponents, theme } from 'theme/theme';
import type { CustomPage } from 'types/page';

// eslint-disable-next-line @typescript-eslint/no-require-imports
import { GlobalSiteStyling } from '../theme/GlobalSiteStyling';

import packageJson from '../../package.json';

console.log(`Version ${packageJson.version}`);

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

type WithProps = {
  children: JSX.Element;
};
const WithGTMProvider = ({ children }: WithProps) => {
  if (config.gtmId) {
    const state = {
      id: config.gtmId,
    };

    return <GTMProvider state={state}>{children}</GTMProvider>;
  }

  return children;
};

function useRouterReady() {
  const [isReady, setIsReady] = useState(false);
  const router = useRouter();

  useEffect(() => {
    setIsReady(router.isReady);
  }, [router.isReady]);

  return isReady;
}

const WithApollo = ({ children }: WithProps) => {
  const client = useApollo();
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

type CustomAppProps = AppProps & {
  Component: CustomPage;
};

const CustomApp = ({ Component, pageProps }: CustomAppProps) => {
  const isRouterReady = useRouterReady();
  const isAuthRequired = !Component.allowUnauthorized;
  // const { clientUrl } = useAuthClientUrl(); // todo: rethink how to integrate this to Auth0

  if (!isRouterReady) return null;
  return (
    <WithGTMProvider>
      <Auth0Provider
        domain={config.authDomain}
        clientId={config.authClientId}
        authorizationParams={{
          redirect_uri: typeof window !== 'undefined' ? window.location.origin : '',
          audience: config.authAudience,
        }}
        cacheLocation="localstorage"
      >
        <WithApollo>
          <TitanThemeProvider components={customComponents}>
            <ThemeProvider theme={theme}>
              <WithAuth isRequired={isAuthRequired}>
                <LeavePromptProvider>
                  <GlobalSiteStyling />
                  <GlobalError />
                  <GlobalSuccess />
                  <OnboardingProvider>
                    <main id="content">
                      <Component {...pageProps} />
                    </main>
                  </OnboardingProvider>
                </LeavePromptProvider>
              </WithAuth>
            </ThemeProvider>
          </TitanThemeProvider>
        </WithApollo>
      </Auth0Provider>
    </WithGTMProvider>
  );
};

// The `config` module in this application uses `publicRuntimeConfig` for its values.
// The documentation of Next JS states the following regarding its use:
//   A page that relies on `publicRuntimeConfig` *must* use `getInitialProps` to
//   opt-out of _Automatic Static Optimization_. Runtime configuration won't be
//   available to any page (or component in a page) without `getInitialProps`.
// https://nextjs.org/docs/api-reference/next.config.js/runtime-configuration
CustomApp.getInitialProps = () => {
  return { pageProps: {} };
};

export default CustomApp;
